import { GCModuleEnum } from '@business/constants/permissions';
import { UserId, UserRole } from '@business/entities/account';
import { ChecklistTemplateResponse, ChecklistId, SortByOptions, SortByDirections } from '@business/entities/checklist';
import { TeamId, TeamResponse } from '@business/entities/team';
import { TaskPriority } from '@business/constants/task';
import { CommonService, TaskService } from '@business/services';
import { FormSelect, FormInput } from '@core/components/form';
import { useHistory, useRouteParam, useQueryStringParam } from '@core/router';
import { useBloc } from '@core/utils/bloc';
import { formControl, formGroup } from '@core/utils/form';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { Session } from '@modules/auth/session';
// import './index.scss';
import { HeaderButtons, PageHeader, Tabs, TabsMapping, TabsHeader, TabHeaderItem } from '@modules/common';
import { openTodoChecklistOverlay } from '@modules/dashboard/DashboardPage/Components/TodoChecklistOverlay';
import { TaskList } from '@modules/dashboard/DashboardPage/TasksMenu/TaskList';
import { openTaskOverlay } from '@modules/tasks/TaskDetailModal';
import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Container, UncontrolledTooltip, Input } from 'reactstrap';
import { TodosPageBloc } from './TodosPageBloc';
import { TaskSearchResults } from './TaskSearchResults';
import { SortEnd } from 'react-sortable-hoc';
import { AppConfig } from '@core/utils/config';
import { Endpoint } from '@business/api/endpoint';
import { CalendarTaskList } from '@modules/dashboard/DashboardPage/TasksMenu/CalendarTaskList';
import { TodoTasksRequest, TodoTaskStatus } from '@business/entities/task';

function defineUserTeamForm() {
  return formGroup({
    id: formControl<UserId | TeamId>({
      placeholder: 'Select User',
    }),
    sortBy: formControl<SortByOptions>({
      placeholder: 'Sort By',
    }),
    sortByDirection: formControl<SortByDirections>({
      placeholder: 'Direction',
    }),
    search: formControl<string>({
      placeholder: 'Search tasks and comments...',
    }),
    priority: formControl<TaskPriority>({
      placeholder: 'Select Priority',
    }),
  });
}

export const TodosPage = memo(() => {
  const appId = useRouteParam('appId', true);

  useEffect(() => {
    if (!R.isEmpty(appId)) {
      AppConfig.set({ appId });
      Endpoint.setAppId(appId);
    }
  }, [appId]);

  const [currentTab, setCurrentTab] = useState(0);
  const status = useMemo(() => (currentTab === 0 ? TodoTaskStatus.pending : TodoTaskStatus.completed), [currentTab]);
  const checklistId = useRouteParam('checklistId', true);
  const form = useMemo(() => defineUserTeamForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(TodosPageBloc, checklistId);
  const tasks = useObservable(bloc.tasks$, []);
  const checklists = R.filter(useObservable(bloc.checklists$, []), c => c?.recurring === 'one-time' || !c.isParent);
  const users = useObservable(bloc.allUsers$, []);
  const teams = useObservable(bloc.teams$, []);
  const history = useHistory();
  const [uid, setUid] = useState('');
  const taskId = useRouteParam('taskId', true);
  const [cTaskId, setCTaskId] = useState('');
  const user = useObservable(Session.user$);
  const permission = R.find(user?.permissions || [], p => p.module === GCModuleEnum.todo);
  const permissionIds = permission?.ids || [];
  const taskStatuses = useObservable(bloc.taskStatuses$, []);
  const [isCalendar, setCalendar] = useState(false);
  const isMe = useMemo(() => permissionIds.includes('-1'), [permissionIds]);
  const [isChecklistOpen, setChecklistOpen] = useState(false);
  const taskCount = useObservable(bloc.taskCount$, { pending: 0, completed: 0 });

  // Text search observables and debouncing
  const searchResults = useObservable(bloc.searchResults$, null);
  const isSearching = useObservable(bloc.isSearching$, false);
  const currentSearchQuery = useObservable(bloc.searchQuery$, '');
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // const myTeams = useMemo(
  //   () =>
  //     R.filter(teams, t => {
  //       return R.find(t.users, u => u.id === user?.id);
  //     }),
  //   [teams, user],
  // ) as TeamResponse[];
  // const teamsToUse = useMemo(() => (isMe ? myTeams : teams), [isMe, myTeams, teams]);

  const userTeamItems = useMemo(
    () =>
      CommonService.selectGroupedItems(
        [
          { label: 'All Users', items: !isMe ? [{ label: 'All', value: `all` }] : [] },
          {
            label: 'Users',
            items: R.filter(
              users,
              u =>
                (R.includes(permissionIds, 'all') ||
                  R.intersection(permissionIds, u.teams).length > 0 ||
                  u.id === user?.id ||
                  user?.role === UserRole.admin) &&
                (u.role === UserRole.admin || u.role === UserRole.user),
            ).map(i => ({ label: CommonService.getFullName(i), value: `user-${i.id}` })),
          },
          { label: 'Teams', items: !isMe ? teams.map(i => ({ label: i.name, value: `team-${i.id}` })) : [] },
        ],
        'label',
        s => s.items,
        'label',
        'value',
      ),
    [isMe, users, teams, permissionIds, user],
  );

  const onClose = useCallback(() => {
    setCTaskId('');
    setChecklistOpen(false);
    history.push(`/todos/${status}`);

    bloc.onTodoReload({ ...formValue, status });
    if (formValue?.id) {
      bloc.search(formValue.id).subscribe();
    }
  }, [bloc, history, formValue, status]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const newArray = R.move(tasks, sort.oldIndex, sort.newIndex);
      const sortedIds = newArray.map((item, index) => ({ id: item.id, menuOrder: index }));
      bloc.orderTasks(sortedIds).subscribe();
    },
    [bloc, tasks],
  );

  useEffect(() => {
    if (taskId && !R.isEmpty(taskId.toString()) && cTaskId !== taskId) {
      setCTaskId(taskId);
      openTaskOverlay({ taskId: taskId, onClose, assignee: [formValue.id], onSortEnd });
    }
  }, [bloc, cTaskId, formValue.id, onClose, onSortEnd, taskId]);

  useEffect(() => {
    if (checklistId && !R.isEmpty(checklistId.toString())) {
      openTodoChecklistOverlay({
        checklistId: checklistId,
        onClose,
      });
    }
  }, [bloc, checklistId, history, onClose]);

  useEffect(() => {
    if (user) {
      form.patchValue({
        id: `user-${user.id}`,
      });
    }
  }, [form, user]);

  const taskCounts = useMemo(() => {
    const overdue = TaskService.getOverdueTasksCount(tasks);
    const pending = taskCount.pending;
    const completed = taskCount.completed;

    return { overdue, pending, completed };
  }, [tasks, taskCount]);

  const checklistCount = useMemo(() => {
    // const pending = checklists.filter(c => {
    //   return c.isCompleted === false;
    // }).length;
    // const completed = checklists.filter(c => {
    //   return c.isCompleted;
    // }).length;
    const pending = TaskService.getFilteredChecklists(checklists, { isCompleted: false }).length;
    const completed = TaskService.getFilteredCompletedChecklists(checklists).length;

    return { pending, completed };
  }, [checklists]);

  const completedCount = useMemo(() => {
    return taskCounts.completed + checklistCount.completed;
  }, [taskCounts.completed, checklistCount.completed]);

  const pendingCount = useMemo(() => {
    return taskCounts.pending + checklistCount.pending;
  }, [taskCounts.pending, checklistCount.pending]);

  const onCreate = useCallback(
    (id: ChecklistTemplateResponse, isChecklist: boolean) => {
      if (isChecklist && !R.isEmpty(uid.toString())) {
        bloc.search(uid).subscribe();
      }
    },
    [bloc, uid],
  );

  const onAssign = useCallback(
    (type: string, checklistId: ChecklistId, assignees: string[]) => {
      return bloc.updateAssignne(type, checklistId, assignees || []).subscribe();
    },
    [bloc],
  );

  // Debounced search handler
  const handleSearchChange = useCallback(
    (params: TodoTasksRequest) => {
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for debounced search
      searchTimeoutRef.current = setTimeout(() => {
        bloc.performTextSearch(params);
      }, 300);
    },
    [bloc],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {}, [formValue?.search]);

  useEffect(() => {
    handleSearchChange(formValue);
    // eslint-disable-next-line
  }, [formValue?.search, handleSearchChange]);

  useEffect(() => {
    if (formValue?.id || formValue?.priority || formValue?.sortBy) {
      bloc.onTodoReload({ ...formValue, status });

      if (formValue?.id) {
        bloc.fetchChecklists(formValue?.id, status).subscribe();
        bloc.search(formValue.id).subscribe();
      }
    }
  }, [bloc, formValue, status]);

  const tabs: TabHeaderItem[] = useMemo(
    () => [
      {
        title: 'Your Tasks',
        rightHTML: (
          <>
            {pendingCount > 0 && <span className="text-dark ml-1"> {pendingCount} </span>}
            {taskCounts.overdue > 0 && <span className="text-danger ml-1">({taskCounts.overdue})</span>}
          </>
        ),
        route: 'pending',
      },
      {
        title: 'Completed',
        rightHTML: <>{completedCount > 0 && <span className="text-dark ml-1"> {completedCount}</span>}</>,

        route: 'completed',
      },
    ],
    [pendingCount, taskCounts.overdue, completedCount],
  );

  const buttons: HeaderButtons[] = useMemo(() => {
    return [
      {
        children: (
          <div key="btn0" style={{ width: 200, marginRight: 10 }}>
            <FormSelect isClearable control={form.controls.id} items={userTeamItems} />
          </div>
        ),
      },
      {
        children: (
          <div key="btn1" style={{ width: 200, marginRight: 10 }}>
            <FormSelect
              isClearable
              control={form.controls.sortBy}
              onChange={() => form.patchValue({ sortByDirection: SortByDirections.asc })}
              items={TaskService.getAllSortByItems()}
            />
          </div>
        ),
      },
      {
        children: (
          <div key="btn2" style={{ width: 200, marginRight: 10 }}>
            <FormSelect isClearable control={form.controls.sortByDirection} items={TaskService.getAllSortByDirectionItems()} />
          </div>
        ),
      },
      {
        children: (
          <div key="btn3" style={{ width: 200, marginRight: 10 }}>
            <FormSelect isClearable control={form.controls.priority} items={TaskService.getAllPriorityItems(false)} />
          </div>
        ),
      },
      {
        children: (
          <div key="search-input" style={{ width: 300, marginRight: 10 }}>
            <div className="position-relative">
              <FormInput
                control={form.controls.search}
                placeholder="Search tasks and comments..."
                style={{ paddingRight: isSearching ? '35px' : undefined }}
              />
              {isSearching && (
                <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                  <i className="fa fa-spinner fa-spin text-muted"></i>
                </div>
              )}
            </div>
          </div>
        ),
      },
    ];
  }, [form, userTeamItems, isSearching]);

  const onRoute = useCallback(
    (tab: string) => {
      if (checklistId) {
        return `/todos/${tab}/checklist/${checklistId}`;
      }
      return `/todos/${tab}`;
    },
    [checklistId],
  );

  if (searchResults) {
    return (
      <div className="page-content">
        <Container fluid>
          <PageHeader buttons={buttons}>Tasks</PageHeader>
          <div id="task-menu-page" className="board-list-task" style={{ margin: 0 }}>
            <TaskSearchResults results={searchResults} isLoading={isSearching} onClose={() => form.patchValue({ search: '' })} />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <PageHeader buttons={buttons}>Tasks</PageHeader>
        <div id="task-menu-page" className="board-list-task" style={{ margin: 0 }}>
          <div className="main_tabs">
            <TabsHeader
              pills
              tabs={tabs}
              route={tab => onRoute(tab)}
              routeParam="type"
              onChange={tab => setCurrentTab(tab)}
              right={
                <div>
                  <div className="d-flex" key="view1">
                    <span className="d-flex hover-pointer ml-3" id="grid_view" onClick={() => setCalendar(true)}>
                      <i className={`fal fa-calendar-alt font-size-20 ${isCalendar ? 'text-primary' : 'text-dark'}`}></i>
                      <UncontrolledTooltip placement="top" target={'grid_view'}>
                        Calendar View
                      </UncontrolledTooltip>
                    </span>
                    <span className="d-flex hover-pointer ml-3" id="list_view" onClick={() => setCalendar(false)}>
                      <i className={`fal fa-th-list font-size-20 ${!isCalendar ? 'text-primary' : 'text-dark'}`}></i>
                      <UncontrolledTooltip placement="top" target={'list_view'}>
                        List View
                      </UncontrolledTooltip>
                    </span>
                  </div>
                </div>
              }
            />

            {/* Regular Task Lists (only show when not searching) */}
            {!isCalendar && currentTab === 0 && (
              <TaskList
                bloc={bloc as any}
                isCompleted={false}
                assignee={[formValue.id]}
                onCreate={onCreate}
                onAssign={onAssign}
                parentSectionId="task-menu-page"
                sortBy={formValue.sortBy}
                sortByDirection={formValue.sortByDirection}
                taskStatuses={taskStatuses}
                showSubtasks={!R.isEmpty(R.toString(formValue?.priority || '')) ? true : false}
                hasPagination
              />
            )}
            {!isCalendar && currentTab === 1 && (
              <TaskList
                bloc={bloc as any}
                isCompleted={true}
                assignee={[formValue.id]}
                onAssign={onAssign}
                parentSectionId="task-menu-page"
                sortBy={formValue.sortBy}
                sortByDirection={formValue.sortByDirection}
                taskStatuses={taskStatuses}
                showSubtasks={!R.isEmpty(R.toString(formValue?.priority || '')) ? true : false}
                hasPagination
              />
            )}
            {isCalendar && <CalendarTaskList allTasks={tasks} bloc={bloc} isCompleted={currentTab === 1} />}

            {/* <Tabs pills tabs={tabs} route={tab => `/todos/${tab}`} routeParam="type" /> */}
          </div>
        </div>
      </Container>
    </div>
  );
});
